import React, { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { ConsoleProps, ConsoleRef, ConsoleOutput } from './types';
import './styles.css';

const Console = forwardRef<ConsoleRef, ConsoleProps>(
  ({ output, height = 300, theme = 'dark', onClear, className, style }, ref) => {
    const consoleRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      addOutput: (output: ConsoleOutput) => {
        // This is handled by the parent component through state
      },
      clear: () => {
        onClear?.();
      },
      scrollToBottom: () => {
        if (consoleRef.current) {
          consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
        }
      },
    }));

    // Auto-scroll to bottom when new output is added
    useEffect(() => {
      if (consoleRef.current) {
        consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
      }
    }, [output]);

    const getMessageIcon = (type: ConsoleOutput['type']) => {
      switch (type) {
        case 'error':
          return '❌';
        case 'warn':
          return '⚠️';
        case 'info':
          return 'ℹ️';
        case 'success':
          return '✅';
        case 'log':
        default:
          return '📝';
      }
    };

    const getMessageClass = (type: ConsoleOutput['type']) => {
      return `console-message console-message-${type}`;
    };

    const formatTimestamp = (timestamp: Date) => {
      return timestamp.toLocaleTimeString();
    };

    return (
      <div
        className={`console-container ${theme} ${className || ''}`}
        style={{ height, ...style }}
      >
        <div className="console-header">
          <h3>Console Output</h3>
          <button 
            className="console-clear-btn"
            onClick={onClear}
            title="Clear console"
          >
            🗑️ Clear
          </button>
        </div>
        
        <div 
          ref={consoleRef}
          className="console-output"
        >
          {output.length === 0 ? (
            <div className="console-empty">
              <p>No console output yet. Run your code to see results here.</p>
            </div>
          ) : (
            output.map((item) => (
              <div key={item.id} className={getMessageClass(item.type)}>
                <span className="console-timestamp">
                  [{formatTimestamp(item.timestamp)}]
                </span>
                <span className="console-icon">
                  {getMessageIcon(item.type)}
                </span>
                <span className="console-message-text">
                  {item.message}
                </span>
                {item.data && (
                  <pre className="console-data">
                    {JSON.stringify(item.data, null, 2)}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }
);

Console.displayName = 'Console';

export default Console;
